import { FC, useState, useMemo } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { TabMenuProfile } from 'components/TabMenuProfile';
import { Summary } from './Summary';
import { Employees } from './Employees';
import styles from './TabProfile.module.scss';
import { Reports } from './Reports';
import { Rate } from './Rate';
import { OrderTemplates } from './OrderTemplates';
import { Button } from 'ui/Button';
import iconPlus from './img/iconPlus.svg';
import { CreateOrderModal } from './CreateOrderModal';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import { Settings } from './Settings';
import { CreateEmployeeModal } from './CreateEmployeeModal/CreateEmployeeModal';
import { useCanManage } from 'hooks/useCanManage';
import { useGetInfoUserQuery, useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { TabsTypes } from 'redux/Api/profileApi/type';
import { Categories } from './Categories';
import { CreateEmployedsСategory } from '../CreateEmployedsСategory';

interface TabProfileTypes {
  tabsData: TabsTypes[];
  openNav: boolean | undefined;
}

export const TabProfile: FC<TabProfileTypes> = ({ tabsData, openNav }) => {
  const categoriesFormDrawer = useMediaQuery('(max-width: 768px)') ?? false;

  const canManage = useCanManage();
  const { data } = useGetProfileQuery();
  const { data: userData } = useGetInfoUserQuery();

  const isUserEmployeeInCompany = useMemo(() => {
    if (!userData?.roles || !data?.id) return false;

    const companyId = data.id.toString();
    const isEmployee = userData.roles.some(
      (role: string) => role === `employee-${companyId}`,
    );

    return isEmployee;
  }, [userData?.roles, data?.id]);

  const filteredTabsData = useMemo(() => {
    if (!tabsData) return [];

    if (isUserEmployeeInCompany) {
      const filtered = tabsData.filter((tab) => tab.key !== 'employees');
      return filtered;
    }

    return tabsData;
  }, [tabsData, isUserEmployeeInCompany]);

  const [active, setActive] = useState<string>('summary');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showCreateCategory, setShowCreateCategory] = useState<boolean>(false);
  const [categoriesListKey, setCategoriesListKey] = useState(0);
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationText, setNotificationText] = useState('');

  const handleSuccess = (message?: string) => {
    setNotificationText(message || 'Операция выполнена успешно');
    setShowNotification(true);
  };

  const content: Record<string, React.ReactNode> = {
    summary: <Summary title={active} />,
    ['monthly-reports']: <Reports openNav={openNav} />,
    staff: <Employees title={active} />,
    presets: <OrderTemplates openNav={openNav} onSuccess={handleSuccess} />,
    categories: (
      <Categories
        key={categoriesListKey}
        openNav={openNav}
        setShowCopiedSuccess={setShowNotification}
        textSuccess={handleSuccess}
      />
    ),
    rate: <Rate />,
    employees: <Employees openNav={openNav} />,
    settings: <Settings />,
  };

  const getActionButton = () => {
    if (!canManage) return null;

    if (active === 'presets') {
      return (
        <Button
          icon
          src={iconPlus}
          theme='outline'
          className={styles.createButton}
          onClick={() => setShowCreateModal(true)}>
          Создать шаблон
        </Button>
      );
    }

    if (active === 'categories') {
      return (
        <Button
          icon
          src={iconPlus}
          theme='outline'
          className={styles.createButton}
          onClick={() => setShowCreateCategory(true)}>
          Создать категорию
        </Button>
      );
    }

    if (active === 'employees') {
      return (
        <Button
          icon
          src={iconPlus}
          theme='outline'
          className={styles.createButton}
          onClick={() => setShowCreateEmployeeModal(true)}>
          Добавить представителя
        </Button>
      );
    }

    return null;
  };

  return (
    <div className={styles.root}>
      <TabMenuProfile
        lists={filteredTabsData}
        active={active}
        setActive={setActive}
        action={getActionButton()}
      />

      {filteredTabsData && <div className={styles.content}>{content[active]}</div>}
      {showCreateModal && (
        <CreateOrderModal setOpen={setShowCreateModal} onSuccess={handleSuccess} />
      )}
      {showCreateCategory && (
        <CreateEmployedsСategory
          variant={categoriesFormDrawer ? 'drawer' : 'modal'}
          setIsShowFinishModal={setShowCreateCategory}
          setShowCopiedSuccess={() => handleSuccess('Создана новая категория')}
          onCategoryCreated={() => setCategoriesListKey((k) => k + 1)}
        />
      )}
      {showCreateEmployeeModal && (
        <CreateEmployeeModal
          setOpen={setShowCreateEmployeeModal}
          onSuccess={() => handleSuccess('Приглашение отправлено')}
        />
      )}
      {showNotification && (
        <SuccessErrorInfo
          type='success'
          text={notificationText}
          handleClose={() => setShowNotification(false)}
        />
      )}
    </div>
  );
};
